/**
 * LicenseActivationPage — shown when the app cannot confirm a valid license.
 *
 * Responsibilities:
 * - Accept a license key and trigger online verification
 * - Provide offline activation fallback (enterprise)
 * - Show machine ID for vendor support
 */

import React, { useState } from 'react';
import { KeyRound, WifiOff, RefreshCw, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { useLicense } from '../context/LicenseContext';

const inputClass =
  'auth-input w-full rounded px-3 py-2 font-mono text-sm tracking-wider placeholder-opacity-50';
const labelClass = 'auth-label mb-1 block text-left text-xs font-medium uppercase tracking-wide';

export default function LicenseActivationPage() {
  const { licenseKey, setLicenseKey, isChecking, error, verify, activateOffline, licenseState } =
    useLicense();

  const [offlineToken, setOfflineToken] = useState('');
  const [showOffline, setShowOffline] = useState(false);
  const [offlineMsg, setOfflineMsg] = useState('');
  const [offlineError, setOfflineError] = useState('');
  const [machineId, setMachineId] = useState<string | null>(null);
  const [copiedMachineId, setCopiedMachineId] = useState(false);

  // Lazy-load machine ID only when user expands offline section
  function handleToggleOffline() {
    const next = !showOffline;
    setShowOffline(next);
    if (next && !machineId) {
      // Read from window.electronAPI or localStorage
      const electronAPI = (window as any).electronAPI;
      if (electronAPI?.getMachineId) {
        electronAPI.getMachineId().then((id: string) => setMachineId(id)).catch(() => {
          const stored = localStorage.getItem('wf_machine_fingerprint');
          setMachineId(stored ?? '—');
        });
      } else {
        const stored = localStorage.getItem('wf_machine_fingerprint');
        setMachineId(stored ?? '—');
      }
    }
  }

  function copyMachineId() {
    if (!machineId) return;
    navigator.clipboard.writeText(machineId).then(() => {
      setCopiedMachineId(true);
      setTimeout(() => setCopiedMachineId(false), 2000);
    });
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    await verify();
  }

  async function handleOfflineActivate(e: React.FormEvent) {
    e.preventDefault();
    setOfflineMsg('');
    setOfflineError('');
    const result = await activateOffline(offlineToken);
    if (result.success) {
      setOfflineMsg(result.message);
    } else {
      setOfflineError(result.message);
    }
  }

  const statusMessage = licenseState
    ? licenseState.valid
      ? null
      : 'Your license could not be validated. Enter your license key to continue.'
    : 'A valid license is required to use DataFlow.';

  return (
    <div className="auth-screen min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-4">

        {/* Header */}
        <div className="text-center space-y-2 mb-6">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded"
               style={{ background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)' }}>
            <KeyRound size={24} />
          </div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            License Activation
          </h1>
          {statusMessage && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {statusMessage}
            </p>
          )}
        </div>

        {/* Online activation card */}
        <div className="auth-card rounded p-6 space-y-4">
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className={labelClass} htmlFor="license-key">
                License Key
              </label>
              <input
                id="license-key"
                className={inputClass}
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                autoComplete="off"
                spellCheck={false}
                disabled={isChecking}
              />
            </div>

            {error && (
              <p className="text-xs rounded px-3 py-2"
                 style={{ background: 'color-mix(in srgb, var(--risk-critical) 10%, transparent)', color: 'var(--risk-critical)' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isChecking || !licenseKey.trim()}
              className="auth-primary-button w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {isChecking ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Verifying…
                </>
              ) : (
                <>
                  <KeyRound size={14} />
                  Activate License
                </>
              )}
            </button>
          </form>
        </div>

        {/* Offline activation (collapsible) */}
        <div className="auth-card rounded overflow-hidden">
          <button
            type="button"
            onClick={handleToggleOffline}
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: 'var(--text-secondary)' }}
          >
            <span className="flex items-center gap-2">
              <WifiOff size={14} />
              Offline Activation (Enterprise)
            </span>
            {showOffline ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showOffline && (
            <div className="px-5 pb-5 space-y-4 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
              {/* Machine ID for the vendor */}
              {machineId && (
                <div className="space-y-1 pt-3">
                  <p className={labelClass}>Your Machine ID</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded px-3 py-2 text-xs font-mono truncate"
                          style={{ background: 'var(--bg-surface-3)', color: 'var(--text-secondary)' }}>
                      {machineId}
                    </code>
                    <button
                      type="button"
                      onClick={copyMachineId}
                      className="auth-secondary-button flex items-center gap-1 px-3 py-2 text-xs rounded"
                      title="Copy machine ID"
                    >
                      {copiedMachineId ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Share this ID with your vendor to receive an offline activation token.
                  </p>
                </div>
              )}

              {/* Offline token input */}
              <form onSubmit={handleOfflineActivate} className="space-y-3">
                <div>
                  <label className={labelClass} htmlFor="offline-token">
                    Offline Activation Token
                  </label>
                  <textarea
                    id="offline-token"
                    rows={3}
                    className={`${inputClass} resize-none`}
                    value={offlineToken}
                    onChange={(e) => setOfflineToken(e.target.value.trim())}
                    placeholder="Paste your offline activation token here…"
                    spellCheck={false}
                  />
                </div>

                {offlineError && (
                  <p className="text-xs rounded px-3 py-2"
                     style={{ background: 'color-mix(in srgb, var(--risk-critical) 10%, transparent)', color: 'var(--risk-critical)' }}>
                    {offlineError}
                  </p>
                )}
                {offlineMsg && (
                  <p className="text-xs rounded px-3 py-2"
                     style={{ background: 'color-mix(in srgb, var(--risk-ok) 10%, transparent)', color: 'var(--risk-ok)' }}>
                    {offlineMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!offlineToken}
                  className="auth-secondary-button w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  <WifiOff size={14} />
                  Apply Offline Token
                </button>
              </form>
            </div>
          )}
        </div>

        <p className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>
          Need a license? Contact{' '}
          <span style={{ color: 'var(--brand-primary)' }}>support@workflowmanager.app</span>
        </p>
      </div>
    </div>
  );
}
